import { SearchFilterTag } from '@/components';
import type {
  ProjectSearchFormType,
  ProjectSearchKeys,
} from '@/features/search/_models/projects-search';
import { t } from '@/i18n';
import { SheetTypeLabelModifier } from '@/utils/enum';
import type { ExtractKeyOf } from '@/utils/utils';
import { type FC, useMemo } from 'react';
import { type FieldPathValue, useFormContext, useWatch } from 'react-hook-form';

type FieldKey = (typeof ProjectSearchKeys)[number];

type HiddenFieldKey = ExtractKeyOf<
  FieldKey,
  | 'amountSearchConditions'
  | 'isSearchTargetProjectName'
  | 'q'
  | 'fundSheetType'
  | 'nestedOrganizationIds'
  | 'nestedOtherResponsibleOrganizationIds'
  | 'nestedPolicyIds'
  | 'nestedExpenditureBudgetPurposeIds'
  | 'corporateNumber'
  | 'categories'
  | 'implementationMethods'
  | 'accountingType'
  | 'accountingTypeIds'
  | 'skipAdministrativeReformTeamEvaluation'
  | 'isPublished'
  | 'period'
  | 'inspectionByExternalExpertReviewType'
  | 'isCandidateOfInspectionByExternalExpert'
  | 'lastImplementedFiscalYearPeriod'
>;
const hiddenFields: HiddenFieldKey[] = [
  'amountSearchConditions',
  'isSearchTargetProjectName',
  'q',
  'fundSheetType',
  'nestedOrganizationIds',
  'nestedOtherResponsibleOrganizationIds',
  'nestedPolicyIds',
  'nestedExpenditureBudgetPurposeIds',
  'corporateNumber',
  'categories',
  'implementationMethods',
  'accountingType',
  'accountingTypeIds',
  'skipAdministrativeReformTeamEvaluation',
  'isPublished',
  'period',
  'inspectionByExternalExpertReviewType',
  'isCandidateOfInspectionByExternalExpert',
  'lastImplementedFiscalYearPeriod',
];

type Props = { field: FieldKey };
const getValue = <K extends FieldKey>(targetValue: any) =>
  targetValue as FieldPathValue<ProjectSearchFormType, K>;

export const CommonFilterTag: FC<Props> = ({ field }) => {
  const { setValue, control } = useFormContext<ProjectSearchFormType>();
  const targetValue = useWatch({ control, name: field });
  const value = useMemo(() => {
    if (!targetValue || hiddenFields.includes(field)) {
      return null;
    }
    switch (field) {
      case 'fiscalYear': {
        const value = getValue<typeof field>(targetValue);
        return t('prefix-fiscal-year-with-value', { value: value?.year() });
      }
      case 'sheetType': {
        const value = getValue<typeof field>(targetValue);
        return SheetTypeLabelModifier[value];
      }
      case 'q': {
        const value = getValue<typeof field>(targetValue);
        return value;
      }
      case 'projectNumbers': {
        const value = getValue<typeof field>(targetValue);
        return `${t('project-number')}: ${value}`;
      }
      default:
        return field;
    }
  }, [field, targetValue]);
  if (!value) {
    return null;
  }
  const isSheetType = field === 'sheetType';
  const isFiscalYear = field === 'fiscalYear';
  return (
    <SearchFilterTag
      label={value}
      closable={!isSheetType && !isFiscalYear}
      onClose={() => {
        setValue(field, null as any);
      }}
    />
  );
};
